import type FreeBusyRow from '../schema/FreeBusyRow';
import { emptyFreeBusyRow } from '../schema/FreeBusyRow';
import FreeBusyType from '../schema/FreeBusyType';
import getMostSevereFreeBusy from '../utils/getMostSevereFreeBusy';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * BASELINE. Do not copy and paste!
 *	> 'lodash-es/debounce' import is restricted from being used by a pattern. Lodash can cause bundle bloat and performance problems with loaf. Use native function instead */
import debounce from 'lodash-es/debounce';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Using transaction to mitigate a perf issue; this should be refactored to use idiomatic
 * Satchel/MobX patterns */
import { ObservableMap, transaction, observable } from 'mobx';
import { isConnectedAccount } from 'owa-accounts-store';
import type { PageQueryResponse } from 'owa-availability-service';
import { lazyGetAvailabilityFunction } from 'owa-availability-service';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo } from 'owa-client-ids';
import { getCalendarAccountSources } from 'owa-account-source-list-store';
import { MILLISECONDS_IN_DAY, MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import { MruCache } from 'owa-mru-cache';
import type {
    GetScheduleFlexibleWorkingHoursView,
    ResponseType,
    ScheduleItem,
} from 'owa-graph-schema';
import type WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getAccountScopeUserSettings } from 'owa-session-store';
import { trace } from 'owa-trace';
import { mutatorAction } from 'satcheljs';
import type { OwaDate } from 'owa-datetime';
import {
    utcDate,
    getTimestamp,
    isEqual,
    addDays,
    addMilliseconds,
    isBefore,
    isAfter,
    startOfDay,
    differenceInDays,
} from 'owa-datetime';
import { SLOT_SIZE_IN_MINUTES } from '../constants';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import {
    convertScheduleItemToCalendarEvent,
    convertGetScheduleWorkingHoursToWorkingHoursType,
} from 'owa-calendar-scheduleitem-converters';
import { logUsage } from 'owa-analytics';
import { isGetScheduleEnabled } from 'owa-feature-flags';
import mergeFlexibleWorkingHours from '../utils/mergeFlexibleWorkingHours';
import { updateScheduleItemIdsForCache } from '../utils/scheduleItemIdUtils';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * With support for additional (shared/delegate) mailboxes as accounts userIdentity can no longer be counted on to uniquely identify an account. Replace userIdentity usage with AccountKey usage.
 *	> 'getMailboxInfoForUserIdentity' import from 'owa-account-source-list' is restricted. UserIdentity does not uniquely identify a mailbox. Use getMailboxInfoByAccountKey or getCoprincipalAccountForAccountKey instead. */
import { getMailboxInfoForUserIdentity } from 'owa-account-source-list';

// This class is part of some deeply embedded Satchel/MobX/React anti-patterns and should be refactored (bug #242008)
export default class FreeBusyCacheManager {
    private readonly epoch: OwaDate;
    private readonly epochTime: number;
    private readonly pageSizeInMilliseconds: number;
    private readonly slotsPerPage: number;
    private readonly cacheSize: number;
    private readonly pageSizeInDays: number;

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2564 (45,13): Property 'store' has no initializer and is not definitely assigned in the constructor.
    // @ts-expect-error
    private store: ObservableMap<string, ObservableMap<string, FreeBusyRow>>;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2564 (49,13): Property 'attendeeDictionary' has no initializer and is not definitely assigned in the constructor.
    // @ts-expect-error
    private attendeeDictionary: {
        [id: string]: MruCache<FreeBusyRow>;
    };
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2564 (53,13): Property 'attendeeFiltersDictionary' has no initializer and is not definitely assigned in the constructor.
    // @ts-expect-error
    private attendeeFiltersDictionary: {
        [id: string]: CalendarEvent;
    };
    private attendeeWorkingHoursDictionary: ObservableMap<string, WorkingHoursType>;
    private attendeeFlexibleWorkingHoursDictionary: ObservableMap<
        string,
        GetScheduleFlexibleWorkingHoursView[]
    >;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2564 (61,13): Property 'initialized' has no initializer and is not definitely assigned in the constructor.
    // @ts-expect-error
    private initialized: boolean;

    // freebusy query queue parameters
    private readonly maxBatchSize: number = 10;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (68,13): Type 'null' is not assignable to type 'OwaDate'.
    // @ts-expect-error
    private pagesRequestedTime: OwaDate = null;
    private requestedAddressesForSamePage: string[] = [];

    constructor(pageSizeInDays: number, cacheSize: number) {
        this.epoch = utcDate(1970, 0, 1);
        this.epochTime = getTimestamp(this.epoch);
        this.cacheSize = cacheSize;
        this.pageSizeInDays = pageSizeInDays;
        this.pageSizeInMilliseconds = pageSizeInDays * MILLISECONDS_IN_DAY;
        this.slotsPerPage =
            this.pageSizeInMilliseconds / (MILLISECONDS_IN_MINUTE * SLOT_SIZE_IN_MINUTES);

        // These dictionaries will be reset on initialization but need an initial value to avoid
        // strict mode errors
        this.attendeeWorkingHoursDictionary = new ObservableMap<string, WorkingHoursType>();
        this.attendeeFlexibleWorkingHoursDictionary = new ObservableMap<
            string,
            GetScheduleFlexibleWorkingHoursView[]
        >();
    }

    public initialize(
        store:
            | ObservableMap<string, ObservableMap<string, FreeBusyRow>>
            | Map<string, Map<string, FreeBusyRow>>
    ): void {
        this.store = observable(store) as ObservableMap<string, ObservableMap<string, FreeBusyRow>>;
        this.attendeeDictionary = {};
        this.attendeeFiltersDictionary = {};
        this.attendeeWorkingHoursDictionary = new ObservableMap<string, WorkingHoursType>();
        this.attendeeFlexibleWorkingHoursDictionary = new ObservableMap<
            string,
            GetScheduleFlexibleWorkingHoursView[]
        >();
        this.initialized = true;
    }

    public isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Gets the WorkingHours for an attendee
     * @param attendee The attendee for which working hours are requested
     */
    public getAttendeeWorkingHours(attendee: AttendeeType): WorkingHoursType | undefined {
        if (!this.isInitialized()) {
            return undefined;
        }

        const address = attendee.Mailbox?.EmailAddress;
        const defaultWorkingHours = getUserConfiguration().UserOptions?.WorkingHours;
        if (!address) {
            return defaultWorkingHours;
        }
        if (!this.attendeeWorkingHoursDictionary.has(address) && defaultWorkingHours) {
            updateAttendeeWorkingHours(
                this.attendeeWorkingHoursDictionary,
                address,
                defaultWorkingHours
            );
        }
        return this.attendeeWorkingHoursDictionary.get(address);
    }

    public getAttendeeFlexibleWorkingHours(
        attendee: AttendeeType
    ): GetScheduleFlexibleWorkingHoursView[] {
        if (!this.isInitialized()) {
            return [];
        }

        const address = attendee.Mailbox?.EmailAddress;
        const defaultWorkingHours: GetScheduleFlexibleWorkingHoursView[] = [];

        if (!address) {
            return defaultWorkingHours;
        }

        if (!this.attendeeFlexibleWorkingHoursDictionary.has(address)) {
            updateAttendeeFlexibleWorkingHours(
                this.attendeeFlexibleWorkingHoursDictionary,
                address,
                defaultWorkingHours
            );
        }

        return this.attendeeFlexibleWorkingHoursDictionary.get(address) ?? defaultWorkingHours;
    }

    // We want to grab the timezone from WHv2 but if it does not exist, grab it from WHv1
    // Currently assumes that all flexible working hours have the same time zone given that we
    // do not have capability to set future working hours' time zones.
    public getAttendeeTimeZone(attendee: AttendeeType): string | undefined {
        if (!this.isInitialized()) {
            return undefined;
        }

        const flexibleWorkingHours = this.getAttendeeFlexibleWorkingHours(attendee);
        if (flexibleWorkingHours.length > 0) {
            return flexibleWorkingHours[0].start.timeZone.name;
        } else {
            const workingHours = this.getAttendeeWorkingHours(attendee);
            return workingHours?.WorkingHoursTimeZoneId;
        }
    }

    public getAttendeeDetailedEvents(
        attendee: string,
        start: OwaDate,
        end: OwaDate,
        timezoneToSet?: string
    ): CalendarEvent[] {
        if (!this.isInitialized()) {
            return [];
        }

        end = this.scaleRangeAsCacheSize(start, end);
        const tz = timezoneToSet || start.tz;
        const startPageIndex = this.getPageIndex(start);
        const endPageIndex = this.getPageIndex(end);
        const events: CalendarEvent[] = [];

        // Don't use transaction!  (See bug #242008.)
        transaction(() => {
            for (let i = startPageIndex; i <= endPageIndex; i++) {
                const page = this.tryGetFreeBusy(attendee, i);
                // NOTE: Start/End in free-busy item comes in UTC and without offsets.
                // NOTE: It looks like Charm is not being respected;
                //       we always get None in the response.
                const pageEvents = ((page.scheduleItems as ScheduleItem[]) || [])
                    .filter(e => e.status != 'Free')
                    .map(
                        item =>
                            ({
                                ...convertScheduleItemToCalendarEvent(item, page.scheduleId, tz),
                                IsFreeBusyOnly: !item.subject, // we assume if there's no subject we don't have details
                            } as CalendarEvent)
                    );

                events.push(...pageEvents);
            }
        });

        return events;
    }

    /**
     * Gets an array of free busy information for an attendee between the start and end time
     * @param attendee The SMTP address for which free busy is requested
     * @param start The start time for which to retrieve free busy
     * @param end The end time for which to retrieve free busy
     * @param filter An optional item to filter out of the free busy results
     */
    public getDetailedFreeBusy(
        attendee: string,
        start: OwaDate,
        end: OwaDate,
        filter?: CalendarEvent
    ): FreeBusyType[] {
        if (!this.isInitialized()) {
            return [];
        }

        end = this.scaleRangeAsCacheSize(start, end);
        const startPageIndex = this.getPageIndex(start);
        const endPageIndex = this.getPageIndex(end);
        const startRecordIndex = Math.floor(this.getRecordIndex(start));
        let endRecordIndex = Math.ceil(this.getRecordIndex(end));

        const mergedFreeBusy: FreeBusyType[] = [];

        // Special handling for <15 minute events.
        if (startPageIndex == endPageIndex && startRecordIndex == endRecordIndex) {
            endRecordIndex++;
        }

        // Don't use transaction!  (See bug #242008.)
        transaction(() => {
            for (let i = startPageIndex; i <= endPageIndex; i++) {
                let page = this.tryGetFreeBusy(attendee, i, filter).mergedFreeBusy;

                if (i == endPageIndex) {
                    page = page.slice(0, endRecordIndex);
                }
                if (i == startPageIndex) {
                    page = page.slice(startRecordIndex);
                }
                mergedFreeBusy.push(...page);
            }
        });

        return mergedFreeBusy;
    }

    /**
     * Gets the most severe free busy status between start and end time
     * @param attendee: The SMTP address for which Free Busy was requested
     * @param start: The start time of the event
     * @param end: The end time of the event
     * @param isAllDayEvent: true if the event spans the entire day
     * @param filter: An optional CalendarEvent to filter out of the results
     */
    public getAggregatedFreeBusy(
        attendee: string,
        start: OwaDate,
        end: OwaDate,
        isAllDayEvent: boolean,
        filter?: CalendarEvent
    ): FreeBusyType {
        if (!this.isInitialized()) {
            return FreeBusyType.NoData;
        }

        start = isAllDayEvent ? startOfDay(start) : start;
        end = isAllDayEvent ? startOfDay(addDays(end, 1)) : end;
        const mergedFreeBusy = this.getDetailedFreeBusy(attendee, start, end, filter);

        return mergedFreeBusy.reduce(
            (previous, current) => getMostSevereFreeBusy(previous, current),
            FreeBusyType.Loading
        );
    }

    /**
     * Adds new email addresses to the cache
     * @param emailAddress {string} The SMTP address for the new attendee
     */
    private addEmailAddress(emailAddress: string) {
        if (!this.store.has(emailAddress)) {
            setAttendeeStore(this.store, emailAddress);
            const value = this.store.get(emailAddress);
            if (value) {
                this.attendeeDictionary[emailAddress] = new MruCache<FreeBusyRow>(
                    'FreeBusyRow',
                    this.cacheSize
                );
                this.attendeeDictionary[emailAddress].initialize(value);
            }
        }
    }

    /**
     * Gets the page number at which the specified time should be found.
     * @param time: The time to perform the lookup against
     */
    private getPageIndex(time: OwaDate) {
        const timeSinceEpoch = getTimestamp(time) - this.epochTime;
        return Math.floor(timeSinceEpoch / this.pageSizeInMilliseconds);
    }

    /**
     * Gets the row number in a page in which the specified time should be found.
     * @param time: The time to perform the lookup against
     */
    private getRecordIndex(time: OwaDate) {
        const timeSinceEpoch = getTimestamp(time) - this.epochTime;
        return (
            (timeSinceEpoch % this.pageSizeInMilliseconds) /
            (MILLISECONDS_IN_MINUTE * SLOT_SIZE_IN_MINUTES)
        );
    }

    /**
     * Checks to see if free busy information is available for the given attendee and time.
     * If no free busy is available, initializes the whole page to 5 FreeBusyType.NoData and
     * requests fresh information.
     * @param attendee: The attendee email address
     * @param pageIndex: The page index to request
     */
    private tryGetFreeBusy(
        attendee: string,
        pageIndex: number,
        filter?: CalendarEvent
    ): FreeBusyRow {
        const dictionaryKey = pageIndex.toString();

        if (!this.attendeeDictionary[attendee]) {
            this.addEmailAddress(attendee);
        }

        // Once we cached the results of an attendee, further request with different filter won't
        // apply in the same session as we read from cache. Actually querying f/b of one attendee
        // with different filters shouldn't happen in practice. For new events, there is no filter;
        // for existing meeting, the original event should be always applied as the filter for attendees
        // in the original meeting.
        let page = this.attendeeDictionary[attendee].get(dictionaryKey);
        if (!page) {
            // Initialize a full page with Loading
            this.attendeeDictionary[attendee].add(dictionaryKey, {
                mergedFreeBusy: emptyFreeBusyRow(this.slotsPerPage, FreeBusyType.Loading),
                scheduleId: '',
            });

            // Request new data from server
            this.requestFreeBusyPage(attendee, pageIndex, filter);

            page = this.attendeeDictionary[attendee].get(dictionaryKey);
        }

        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (312,9): Type 'FreeBusyRow | undefined' is not assignable to type 'FreeBusyRow'.
        // @ts-expect-error
        return page;
    }

    /**
     * Returns true if the provided event has a full or partial conflict with the provided daterange
     * @param event The CalendarEvent to check for
     * @param start The start of the daterange
     * @param end The end of the daterange
     */
    private isConflict(event: OwaDateRange, start: OwaDate, end: OwaDate) {
        return isAfter(event.end, start) && isBefore(event.start, end);
    }

    /**
     * Attempts to remove the event being edited from the server's free busy results
     * @param freeBusy The freeBusy object, as received from the server
     * @param rangeStart The start date for the provided free busy object
     * @param rangeEnd The end date for the provided free busy object
     * @param attendeeResponse The response for the event for the attendee whos FB is being patched
     */
    private tryFilterOutCalendarEvent(
        freeBusy: FreeBusyRow,
        rangeStart: OwaDate,
        rangeEnd: OwaDate,
        filter: CalendarEvent,
        attendeeResponse: ResponseType
    ) {
        // Only patch the organizer and attendees who have accepted the meeting.
        if (attendeeResponse == 'Accept' || attendeeResponse == 'Organizer') {
            // Compute which portions of the Free Busy need to be patched.
            const start = filter.Start;
            const end = filter.End;
            if (filter && this.isConflict({ start, end }, rangeStart, rangeEnd)) {
                const filterStart = isBefore(start, rangeStart) ? rangeStart : start;
                const filterEnd = isAfter(end, rangeEnd) ? rangeEnd : end;
                const startRecordIndex = this.getRecordIndex(filterStart);
                const endRecordIndex = this.getRecordIndex(filterEnd);

                /*
                / Hypothesis: Organizing or accepting a meeting request is an implicit commitment
                / to be available for the duration of the event. Therefore, marking such time as
                / free is arguably valid.
                /
                / Drawback: This code will also wipe out other events occurring in the same time
                / period. More nuanced filtering would be possible by either:
                /
                / a) Performing the filtering in the server, where complete data is available for
                / every attendee,
                /
                / b) Obtaining an ItemId for each of the items of all the attendees (possibly an
                / information leak) or,
                /
                / c) As a last resort, best effort filtering might also be possible. This would
                / attempt to match based on either an ItemId or times and FreeBusyType depending on
                / access level. After the matching and filtering, the merged free busy can be
                / recomputed from the remaining items.
                */
                for (let i = startRecordIndex; i < endRecordIndex; i++) {
                    freeBusy.mergedFreeBusy[i] = FreeBusyType.Free;
                }
            }
        }
        return freeBusy;
    }

    /**
     * Requests a fresh free busy page from server
     * @param attendee: The email address for which free busy should be requested
     * @param pageIndex: The page index to request
     * @param attendeeDictionary: The dictionary of MRU caches
     * @param filter: An optional CalendarEvent to filter out of the results
     */
    private requestFreeBusyPage(attendee: string, pageIndex: number, filter?: CalendarEvent) {
        // The filter for each attendee f/b request needs to be cached. We batch the
        // requests for attendees' availability. For example, we request the availability
        // of p0, p1 and p2 with filter0, filter1 and filter2 respectively. The three requests
        // could be batched into one and we get the response for three attendees. In the continuation function,
        // we need to make sure the correct filter is applied to the response of each attendee.
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (393,9): Type 'CalendarEvent | undefined' is not assignable to type 'CalendarEvent'.
        // @ts-expect-error
        this.attendeeFiltersDictionary[attendee] = filter;

        this.getAvailabilityForSingleAddress(attendee, pageIndex);
    }

    // Request f/b of single address. If it is organizer, the request will be out immediately;
    // Otherwise, the address is put in a queue. Once the query page index has changed, or the
    // queue reaches the size limit, or an amount of time has elapsed since the first address
    // was added to the queue, we send one request to query the status of all addresses in the queue.
    private getAvailabilityForSingleAddress(address: string, pageIndex: number) {
        const rangeStart = addMilliseconds(this.epoch, this.pageSizeInMilliseconds * pageIndex);
        const rangeEnd = addMilliseconds(rangeStart, this.pageSizeInMilliseconds);

        // The f/b cache isn't specific to a single account, but we just need to confirm that the account in question
        // is present in the store to pass the conditional check for sending the request directly
        const accounts = getCalendarAccountSources();
        const settingsMap = accounts.map(
            account => getAccountScopeUserSettings(account.mailboxInfo).SessionSettings
        );
        const accountMatchIndex = settingsMap.findIndex(
            settings =>
                settings?.UserEmailAddress === address || settings?.UserPrincipalName === address
        );

        if (accountMatchIndex !== -1 || isConnectedAccount(address)) {
            const mailboxInfo =
                accountMatchIndex !== -1
                    ? accounts[accountMatchIndex].mailboxInfo
                    : /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
                       * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
                       *	> Forbidden non-null assertion. */
                      getMailboxInfoForUserIdentity(address)!;
            // Never batch the organizer. As their availability is much less likely to time out.
            lazyGetAvailabilityFunction.importAndExecute(
                rangeStart,
                rangeEnd,
                [address],
                this.processPageQueryResponsesInSamePage(pageIndex, rangeStart, rangeEnd),
                true /*retryOnFail*/,
                mailboxInfo,
                SLOT_SIZE_IN_MINUTES
            );

            return;
        }

        // If there was a page jump, resolve availability for pending attendees and start a new batch.
        if (!isEqual(this.pagesRequestedTime, rangeStart)) {
            this.debouncedGetAvailability.flush();
            this.pagesRequestedTime = rangeStart;
        }

        this.requestedAddressesForSamePage.push(address);
        this.debouncedGetAvailability(
            rangeStart,
            rangeEnd,
            pageIndex,
            this.requestedAddressesForSamePage.slice()
        );

        if (this.requestedAddressesForSamePage.length >= this.maxBatchSize) {
            this.debouncedGetAvailability.flush();
        }
    }

    private debouncedGetAvailability = debounce(
        (start: OwaDate, end: OwaDate, pageIndex: number, attendees: string[]) => {
            this.requestedAddressesForSamePage = [];

            // F/b cache does not currently accept identity for multi-account. Need to
            // revisit this later
            /* eslint-disable-next-line owa-custom-rules/no-default-methods  -- (https://aka.ms/OWALintWiki)
             * Methods that use the default mailbox should not be used, see https://aka.ms/multiaccountlinter
             *	> Do Not Use Default: The getUserMailboxInfo function should not be used for Respond multi-account support, user identity is not enough to identify account information. */
            const mailboxInfo = getUserMailboxInfo();

            lazyGetAvailabilityFunction.importAndExecute(
                start,
                end,
                attendees,
                this.processPageQueryResponsesInSamePage(pageIndex, start, end),
                true, //retryOnFail
                mailboxInfo,
                SLOT_SIZE_IN_MINUTES
            );
        },
        500
    );

    private processPageQueryResponsesInSamePage(
        pageIndex: number,
        rangeStart: OwaDate,
        rangeEnd: OwaDate
    ) {
        return (pageQueryResponse: PageQueryResponse) => {
            if (!pageQueryResponse) {
                return;
            }

            /* eslint-disable-next-line owa-custom-rules/forbid-foreach-with-variables-outside-of-function-scope -- (https://aka.ms/OWALintWiki)
             * https://dev.azure.com/outlookweb/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9650/Use-for-const-loop-of-instead-of-forEach
             *	> When using a forEach function call, avoid using variables outside of the scope of the function, use for (const item of array) instead */
            Object.keys(pageQueryResponse).forEach(address => {
                // It is possible that a new session has started when we get the response.
                // Skip if we don't query the f/b of this address; otherwise, we can still
                // cache the f/b for the particular attendee.
                if (!this.attendeeDictionary[address]) {
                    return;
                }

                const userAvailability = pageQueryResponse[address];
                const { availabilityView, workingHours, flexibleWorkingHours, error } =
                    userAvailability;

                const isGatewayEnabled = isGetScheduleEnabled();

                // Not every error is actually a problem; for example, a 401 Unauthorized is expected
                // if an attendee is not sharing their f/b status, but we should errors regardless.
                if (error) {
                    /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                     * Baseline, this function can't be used inside a loop, please move it outside
                     *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                    logUsage('FreeBusyDataError', {
                        isGatewayEnabled,
                        responseCode: error.responseCode,
                    });
                }

                // Add working hours of attendee to the dictionary if not already added.
                if (this.attendeeWorkingHoursDictionary && workingHours) {
                    updateAttendeeWorkingHours(
                        this.attendeeWorkingHoursDictionary,
                        address,
                        convertGetScheduleWorkingHoursToWorkingHoursType(workingHours)
                    );
                }

                if (this.attendeeFlexibleWorkingHoursDictionary && flexibleWorkingHours) {
                    updateAttendeeFlexibleWorkingHours(
                        this.attendeeFlexibleWorkingHoursDictionary,
                        address,
                        flexibleWorkingHours
                    );
                }

                let freeBusyRow: FreeBusyRow = {
                    ...userAvailability,
                    mergedFreeBusy: availabilityView
                        ? availabilityView.split('').map((value: string) => parseInt(value))
                        : emptyFreeBusyRow(this.slotsPerPage, FreeBusyType.NoData),
                };

                const cachedFilter = this.attendeeFiltersDictionary[address];
                if (cachedFilter) {
                    const attendeeResponses: {
                        [key: string]: ResponseType;
                    } = {};

                    [
                        ...cachedFilter.RequiredAttendees,
                        ...cachedFilter.OptionalAttendees,
                        ...cachedFilter.Resources,
                    ].forEach(eventAttendee => {
                        if (eventAttendee.Mailbox?.EmailAddress && eventAttendee.ResponseType) {
                            attendeeResponses[eventAttendee.Mailbox.EmailAddress] =
                                eventAttendee.ResponseType;
                        }
                    });

                    if (cachedFilter.Organizer?.Mailbox?.EmailAddress) {
                        attendeeResponses[cachedFilter.Organizer.Mailbox.EmailAddress] =
                            'Organizer';
                    }

                    const attendeeResponse = attendeeResponses[address];
                    if (!attendeeResponse) {
                        trace.warn(`Unknown response for attendee ${address}`);
                    }

                    freeBusyRow = this.tryFilterOutCalendarEvent(
                        freeBusyRow,
                        rangeStart,
                        rangeEnd,
                        cachedFilter,
                        attendeeResponse
                    );
                }

                // If event data is retrieved from OWS, the item ID will change with each query.
                // However, if the data comes from the GetSchedule API, the item ID is immutable.
                // An event might be present in two different time windows due to overlap, meaning
                // we would have the same immutable ID duplicated across two cache pages. Furthermore,
                // if the user does not have sufficient permissions for data on the remote calendar,
                // the item ID will be undefined or an empty string. Both of these scenarios introduce
                // risk for React components that use the item ID as a key. For data from the GetSchedule API,
                // the FreeBusyCache appends a random GUID to avoid key duplication. If for any reason, the
                // item ID needs to be sent to the service, use getScheduleItemIdFromCacheItemId().
                if (isGatewayEnabled && !!freeBusyRow.scheduleItems) {
                    updateScheduleItemIdsForCache(freeBusyRow.scheduleItems);
                }

                this.attendeeDictionary[address].add(pageIndex.toString(), freeBusyRow);
            });
        };
    }

    private scaleRangeAsCacheSize(start: OwaDate, end: OwaDate) {
        const cacheSizeInDays = this.cacheSize * this.pageSizeInDays;
        return differenceInDays(end, start) > cacheSizeInDays
            ? addDays(start, cacheSizeInDays)
            : end;
    }
}

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const updateAttendeeWorkingHours = mutatorAction(
    'updateAttendeeWorkingHours',
    (
        attendeeWorkingHoursDictionary: ObservableMap<string, WorkingHoursType>,
        address: string,
        workingHours: WorkingHoursType
    ) => {
        attendeeWorkingHoursDictionary.set(address, workingHours);
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const updateAttendeeFlexibleWorkingHours = mutatorAction(
    'updateAttendeeFlexibleWorkingHours',
    (
        attendeeFlexibleWorkingHoursDictionary: ObservableMap<
            string,
            GetScheduleFlexibleWorkingHoursView[]
        >,
        address: string,
        flexibleWorkingHours: GetScheduleFlexibleWorkingHoursView[]
    ) => {
        if (attendeeFlexibleWorkingHoursDictionary.has(address)) {
            const merged = mergeFlexibleWorkingHours(
                /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
                 * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
                 *	> Forbidden non-null assertion. */
                attendeeFlexibleWorkingHoursDictionary.get(address)!,
                flexibleWorkingHours
            );
            attendeeFlexibleWorkingHoursDictionary.set(address, merged);
        } else {
            attendeeFlexibleWorkingHoursDictionary.set(address, flexibleWorkingHours);
        }
    }
);

/* eslint-disable-next-line owa-custom-rules/require-add-identifier-to-mutator-action-variables -- (https://aka.ms/OWALintWiki)
 * Mutator action variables should end with 'Mutator' so that we can more easily identify potential misuses of it.
 *	> Please add 'Mutator' substring add the end of the mutator action variable name. */
const setAttendeeStore = mutatorAction(
    'setAttendeeStore',
    (store: ObservableMap<string, ObservableMap<string, FreeBusyRow>>, attendee: string) => {
        store.set(attendee, new ObservableMap<string, FreeBusyRow>());
    }
);
